import React, { useState, useEffect, useRef } from 'react';
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { 
  AlertCircle, 
  Brain, 
  BrainCircuit, 
  CheckCircle2, 
  Clock, 
  CreditCard, 
  HeartPulse, 
  Plus, 
  Sparkles, 
  ThumbsUp, 
  Zap, 
  XCircle,
  Info,
  Loader2
} from "lucide-react";
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/lib/supabase';
import { useSubscription } from '@/contexts/SubscriptionContext';
import confetti from 'canvas-confetti';
import { aiService } from '@/services/aiService';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Badge } from "@/components/ui/badge";
import {
  Alert,
  AlertDescription,
  AlertTitle,
} from "@/components/ui/alert";

// Stil für Animations-Klassen
const fadeOutStyle = `
  @keyframes fadeOut {
    from { opacity: 1; transform: translateY(0); }
    to { opacity: 0; transform: translateY(20px); }
  }
  .fadeOut {
    animation: fadeOut 0.3s ease-out forwards;
  }
`;

interface ImpulseCheck {
  id?: string;
  user_id: string;
  item: string;
  reason: string;
  feeling: string;
  alternative: string;
  future_feeling: string;
  price?: number;
  status: 'active' | 'completed' | 'purchased';
  created_at?: string;
  cooldown_ends_at: string;
}

const FEELINGS = [
  { value: 'stressed', label: 'Gestresst', icon: '😫' },
  { value: 'bored', label: 'Gelangweilt', icon: '😴' },
  { value: 'sad', label: 'Traurig', icon: '😢' },
  { value: 'happy', label: 'Glücklich', icon: '😊' },
  { value: 'anxious', label: 'Ängstlich', icon: '😰' },
  { value: 'frustrated', label: 'Frustriert', icon: '😤' },
  { value: 'excited', label: 'Aufgeregt', icon: '🤩' },
  { value: 'insecure', label: 'Unsicher', icon: '🫣' }
];

// Motivierende Sprüche für verschiedene Schritte
const MOTIVATIONAL_QUOTES = {
  general: [
    "Jede bewusste Entscheidung stärkt deine finanzielle Freiheit!",
    "Deine Zukunft wird dir danken!",
    "Kleine Entscheidungen heute, große Wirkung morgen.",
    "Du bist auf dem richtigen Weg zu deinen finanziellen Zielen!",
    "Impulskontrolle ist wie ein Muskel - je mehr du ihn trainierst, desto stärker wird er.",
  ],
  step1: [
    "Großartig, dass du innehältst und nachdekkst!",
    "Bewusstes Konsumieren beginnt mit dem Erkennen des Impulses.",
    "Der erste Schritt zur Kontrolle ist das Benennen.",
  ],
  step2: [
    "Wieviel Arbeitszeit steckt in diesem Preis?",
    "Denk daran: Jeder Euro kann für dich arbeiten oder gegen dich.",
    "Was könntest du mit diesem Geld in einem Jahr haben?",
  ],
  step3: [
    "Unterscheide zwischen Bedürfnis und Wunsch.",
    "Ehrliche Gründe helfen dir, bessere Entscheidungen zu treffen.",
    "Hinterfrage deine Motivation - das ist der Schlüssel zur Kontrolle.",
  ],
  step4: [
    "Emotionen sind oft Treiber für Impulskäufe.",
    "Fühlen kommt vor dem Handeln - erkenne das Muster.",
    "Kaufen wir aus Freude oder um negative Gefühle zu vermeiden?",
  ],
  step5: [
    "Wie wird sich dein zukünftiges Ich fühlen?",
    "Regret Minimization: Minimiere spätere Reue durch Vorausdenken.",
    "Die Freude am Kauf ist oft nur kurz - denk langfristig.",
  ],
  completed: [
    "Glückwunsch! Du hast den vollen Prozess durchlaufen.",
    "Die 24h Abkühlphase gibt dir Raum für Klarheit.",
    "Weise Entscheidung! Die Zeit arbeitet jetzt für dich.",
  ]
};

// Zufälligen Spruch aus der Kategorie auswählen
const getRandomQuote = (category) => {
  const quotes = MOTIVATIONAL_QUOTES[category] || MOTIVATIONAL_QUOTES.general;
  return quotes[Math.floor(Math.random() * quotes.length)];
};

export const ImpulseControl = () => {
  const { user } = useAuth();
  const { subscription } = useSubscription();
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(1);
  const [activeImpulses, setActiveImpulses] = useState<ImpulseCheck[]>([]);
  const [completedImpulses, setCompletedImpulses] = useState<ImpulseCheck[]>([]);
  const [currentImpulse, setCurrentImpulse] = useState<Partial<ImpulseCheck>>({
    user_id: user?.id || '',
    status: 'active',
    item: '',
    reason: '',
    feeling: '',
    alternative: '',
    future_feeling: ''
  });
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState<'active' | 'completed'>('active');
  const [totalSaved, setTotalSaved] = useState(0);
  const [showAllImpulses, setShowAllImpulses] = useState(false);
  const [currentQuote, setCurrentQuote] = useState('');
  const [showAiAdvice, setShowAiAdvice] = useState(false);
  const [aiAdvice, setAiAdvice] = useState<string>('');
  const [isAiProcessing, setIsAiProcessing] = useState(false);
  const [remainingAiRequests, setRemainingAiRequests] = useState(10);

  // Refs für Eingabefelder
  const inputRefs = {
    1: useRef<HTMLInputElement>(null),
    2: useRef<HTMLInputElement>(null),
    3: useRef<HTMLTextAreaElement>(null),
    5: useRef<HTMLTextAreaElement>(null),
  };

  useEffect(() => {
    if (user) {
      fetchImpulseChecks();
      calculateTotalSaved();
      setRemainingAiRequests(aiService.getRemainingRequests());
    }
  }, [user]);

  // Fokussiere auf das aktuelle Eingabefeld, wenn sich der Schritt ändert
  useEffect(() => {
    if (open) {
      // Timeout verwenden, um sicherzustellen, dass das Element gerendert wurde
      setTimeout(() => {
        const currentRef = inputRefs[step as keyof typeof inputRefs];
        if (currentRef?.current) {
          currentRef.current.focus();
        }
      }, 100);
      
      // Setze motivierenden Spruch basierend auf aktuellem Schritt
      const category = `step${step}`;
      setCurrentQuote(getRandomQuote(category));
    }
  }, [step, open]);

  const fetchImpulseChecks = async () => {
    try {
      const { data, error } = await supabase
        .from('impulse_checks')
        .select('*')
        .eq('user_id', user?.id)
        .order('created_at', { ascending: false });

      if (error) throw error;

      const active = data?.filter(i => i.status === 'active') || [];
      const completed = data?.filter(i => i.status !== 'active') || [];
      
      setActiveImpulses(active);
      setCompletedImpulses(completed);
    } catch (error) {
      console.error('Error fetching impulse checks:', error);
    }
  };

  const calculateTotalSaved = async () => {
    try {
      const { data, error } = await supabase
        .from('impulse_checks')
        .select('price')
        .eq('user_id', user?.id)
        .eq('status', 'completed');

      if (error) throw error;

      const total = data?.reduce((sum, item) => sum + (item.price || 0), 0) || 0;
      setTotalSaved(total);
    } catch (error) {
      console.error('Error calculating saved amount:', error);
    }
  };

  const handleNext = () => {
    if (validateCurrentStep()) {
      console.log('Aktueller Schritt:', step);
      if (step < 5) {
        setStep(step + 1);
        console.log('Gehe zu Schritt:', step + 1);
      } else {
        setStep(6); // Explizit zum Zusammenfassungsschritt springen
        console.log('Gehe zum Zusammenfassungsschritt (6)');
      }
    }
  };

  const validateCurrentStep = () => {
    switch (step) {
      case 1:
        if (!currentImpulse.item) {
          toast.error('Bitte gib an, was du kaufen möchtest');
          return false;
        }
        return true;
      case 2:
        if (!currentImpulse.price || currentImpulse.price <= 0) {
          toast.error('Bitte gib einen gültigen Preis ein');
          return false;
        }
        return true;
      case 3:
        if (!currentImpulse.reason) {
          toast.error('Bitte gib einen Grund an');
          return false;
        }
        return true;
      case 4:
        if (!currentImpulse.feeling) {
          toast.error('Bitte wähle ein Gefühl aus');
          return false;
        }
        return true;
      case 5:
        if (!currentImpulse.future_feeling) {
          toast.error('Bitte beschreibe dein zukünftiges Gefühl');
          return false;
        }
        return true;
      default:
        return true;
    }
  };

  const handleSaveImpulseCheck = async () => {
    if (!user) return;
    
    setLoading(true);
    
    try {
      // Berechne das Ende der Abkühlphase (24 Stunden)
      const cooldownEndsAt = new Date();
      cooldownEndsAt.setHours(cooldownEndsAt.getHours() + 24);
      
      const impulseData: ImpulseCheck = {
        ...currentImpulse as ImpulseCheck,
        user_id: user.id,
        status: 'active',
        cooldown_ends_at: cooldownEndsAt.toISOString()
      };
      
      const { data, error } = await supabase
        .from('impulse_checks')
        .insert([impulseData])
        .select();

      if (error) throw error;
      
      // Wähle einen motivierenden Spruch für den Abschluss
      const completionQuote = getRandomQuote('completed');
      
      toast.success(
        <div>
          <div>Impuls-Check gespeichert! 🎉 Die 24h Abkühlphase beginnt jetzt.</div>
          <div className="text-sm italic mt-2">{completionQuote}</div>
        </div>
      );
      
      // Füge den neuen Impuls zur aktiven Liste hinzu
      if (data && data.length > 0) {
        setActiveImpulses(prev => [data[0], ...prev]);
      }
      
      // Reset und Dialog schließen
      setStep(1);
      setCurrentImpulse({
        user_id: user.id,
        status: 'active',
        item: '',
        reason: '',
        feeling: '',
        alternative: '',
        future_feeling: ''
      });
      setShowAiAdvice(false);
      setAiAdvice('');
      setOpen(false);
    } catch (error) {
      console.error('Error saving impulse check:', error);
      toast.error('Fehler beim Speichern des Impuls-Checks');
    } finally {
      setLoading(false);
    }
  };

  const handleCompleteImpulse = async (impulseId: string) => {
    try {
      const { error } = await supabase
        .from('impulse_checks')
        .update({ status: 'completed' })
        .eq('id', impulseId);

      if (error) throw error;
      
      // Lokales Update
      setActiveImpulses(prev => prev.filter(i => i.id !== impulseId));
      const completedItem = activeImpulses.find(i => i.id === impulseId);
      if (completedItem) {
        completedItem.status = 'completed';
        setCompletedImpulses(prev => [completedItem, ...prev]);
        setTotalSaved(prev => prev + (completedItem.price || 0));
      }
      
      // Konfetti-Effekt für erfolgreiche Impulskontrolle
      confetti({
        particleCount: 100,
        spread: 70,
        origin: { y: 0.6 }
      });
      
      // Motivierender Erfolgsspruch
      const successQuote = getRandomQuote('general');
      
      toast.success(
        <div>
          <div>Super! Du hast einen Impulskauf überwunden 🎉</div>
          <div className="text-sm italic mt-2">{successQuote}</div>
          {completedItem?.price && (
            <div className="text-sm font-medium text-green-600 mt-1">
              Gespart: {completedItem.price}€
            </div>
          )}
        </div>
      );
    } catch (error) {
      console.error('Error completing impulse check:', error);
      toast.error('Fehler beim Aktualisieren des Status');
    }
  };

  const handlePurchaseAnyway = async (impulseId: string) => {
    try {
      const { error } = await supabase
        .from('impulse_checks')
        .update({ status: 'purchased' })
        .eq('id', impulseId);

      if (error) throw error;
      
      // Lokales Update
      setActiveImpulses(prev => prev.filter(i => i.id !== impulseId));
      const purchasedItem = activeImpulses.find(i => i.id === impulseId);
      if (purchasedItem) {
        purchasedItem.status = 'purchased';
        setCompletedImpulses(prev => [purchasedItem, ...prev]);
      }
      
      toast.info('Du hast dich für den Kauf entschieden. Kein Problem, jeder braucht ab und zu etwas Neues!');
    } catch (error) {
      console.error('Error updating impulse check:', error);
      toast.error('Fehler beim Aktualisieren des Status');
    }
  };

  const getCooldownTimeLeft = (endsAt: string) => {
    const end = new Date(endsAt);
    const now = new Date();
    const diff = end.getTime() - now.getTime();
    
    if (diff <= 0) return 'Abkühlphase beendet';
    
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    
    return `${hours}h ${minutes}m verbleibend`;
  };

  const getStepContent = () => {
    switch (step) {
      case 1:
        return (
          <div className="space-y-4">
            <div className="text-center">
              <h3 className="text-base font-medium text-gray-900 mb-1">Was möchtest du kaufen?</h3>
              <p className="text-xs text-gray-600 italic mb-3">{currentQuote}</p>
            </div>
            <Input
              ref={inputRefs[1]}
              value={currentImpulse.item || ''}
              onChange={(e) => setCurrentImpulse({ ...currentImpulse, item: e.target.value })}
              placeholder="z.B. Neue Kopfhörer, Kleidungsstück, etc."
              className="w-full bg-white border-gray-200 focus:border-orange-300 focus:ring-orange-200/50 rounded-lg"
            />

            <div className="flex justify-end">
              <Button
                type="button"
                onClick={handleNext}
                size="sm"
                className="bg-orange-600 hover:bg-orange-700 text-white rounded-lg h-8"
              >
                Weiter
              </Button>
            </div>
          </div>
        );
      case 2:
        return (
          <div className="space-y-4">
            <div className="text-center">
              <h3 className="text-base font-medium text-gray-900 mb-1">Wie viel kostet es?</h3>
              <p className="text-xs text-gray-600 italic mb-3">{currentQuote}</p>
            </div>
            <div className="relative">
              <Input
                ref={inputRefs[2]}
                type="number"
                value={currentImpulse.price || ''}
                onChange={(e) => setCurrentImpulse({ ...currentImpulse, price: parseFloat(e.target.value) })}
                placeholder="Preis in €"
                className="w-full pl-8 bg-white border-gray-200 focus:border-orange-300 focus:ring-orange-200/50 rounded-lg"
                min="0"
                step="0.01"
              />
              <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500">€</span>
            </div>

            <div className="flex justify-between">
              <Button
                type="button"
                variant="outline"
                onClick={() => setStep(1)}
                size="sm"
                className="border-gray-200 hover:border-gray-300 rounded-lg h-8"
              >
                Zurück
              </Button>
              <Button
                type="button"
                onClick={handleNext}
                size="sm"
                className="bg-orange-600 hover:bg-orange-700 text-white rounded-lg h-8"
              >
                Weiter
              </Button>
            </div>
          </div>
        );
      case 3:
        return (
          <div className="space-y-4">
            <div className="text-center">
              <h3 className="text-base font-medium text-gray-900 mb-1">Warum möchtest du es kaufen?</h3>
              <p className="text-xs text-gray-600 italic mb-3">{currentQuote}</p>
            </div>
            <Textarea
              ref={inputRefs[3]}
              value={currentImpulse.reason || ''}
              onChange={(e) => setCurrentImpulse({ ...currentImpulse, reason: e.target.value })}
              placeholder="Beschreibe kurz, warum du dieses Produkt kaufen möchtest..."
              className="w-full min-h-[80px] bg-white border-gray-200 focus:border-orange-300 focus:ring-orange-200/50 rounded-lg"
            />

            <div className="flex justify-between">
              <Button
                type="button"
                variant="outline"
                onClick={() => setStep(2)}
                size="sm"
                className="border-gray-200 hover:border-gray-300 rounded-lg h-8"
              >
                Zurück
              </Button>
              <Button
                type="button"
                onClick={handleNext}
                size="sm"
                className="bg-orange-600 hover:bg-orange-700 text-white rounded-lg h-8"
              >
                Weiter
              </Button>
            </div>
          </div>
        );
      case 4:
        return (
          <div className="space-y-4">
            <div className="text-center">
              <h3 className="text-base font-medium text-gray-900 mb-1">Wie fühlst du dich gerade?</h3>
              <p className="text-xs text-gray-600 italic mb-3">{currentQuote}</p>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              {FEELINGS.map((feeling) => (
                <Button
                  key={feeling.value}
                  type="button"
                  variant={currentImpulse.feeling === feeling.value ? "default" : "outline"}
                  className={`h-auto py-3 flex flex-col gap-1 text-xs rounded-lg border transition-all ${
                    currentImpulse.feeling === feeling.value
                      ? "bg-orange-600 hover:bg-orange-700 text-white border-orange-600"
                      : "border-gray-200 hover:border-orange-300 hover:bg-orange-50"
                  }`}
                  onClick={() => setCurrentImpulse({ ...currentImpulse, feeling: feeling.value })}
                >
                  <span className="text-lg">{feeling.icon}</span>
                  <span>{feeling.label}</span>
                </Button>
              ))}
            </div>

            <div className="flex justify-between">
              <Button
                type="button"
                variant="outline"
                onClick={() => setStep(3)}
                size="sm"
                className="border-gray-200 hover:border-gray-300 rounded-lg h-8"
              >
                Zurück
              </Button>
              <Button
                type="button"
                onClick={handleNext}
                size="sm"
                className="bg-orange-600 hover:bg-orange-700 text-white rounded-lg h-8"
              >
                Weiter
              </Button>
            </div>
          </div>
        );
      case 5:
        return (
          <div className="space-y-4">
            <div className="text-center">
              <h3 className="text-base font-medium text-gray-900 mb-1">Wie wirst du dich fühlen, nachdem du es gekauft hast?</h3>
              <p className="text-xs text-gray-600 italic mb-3">{currentQuote}</p>
            </div>
            <Textarea
              ref={inputRefs[5]}
              value={currentImpulse.future_feeling || ''}
              onChange={(e) => setCurrentImpulse({ ...currentImpulse, future_feeling: e.target.value })}
              placeholder="Beschreibe, wie du dich fühlen wirst, nachdem du es gekauft hast..."
              className="w-full min-h-[80px] bg-white border-gray-200 focus:border-orange-300 focus:ring-orange-200/50 rounded-lg"
            />

            <div className="flex justify-between">
              <Button
                type="button"
                variant="outline"
                onClick={() => setStep(4)}
                size="sm"
                className="border-gray-200 hover:border-gray-300 rounded-lg h-8"
              >
                Zurück
              </Button>
              <Button
                type="button"
                onClick={handleNext}
                size="sm"
                className="bg-orange-600 hover:bg-orange-700 text-white rounded-lg h-8"
              >
                Weiter
              </Button>
            </div>
          </div>
        );
      case 6:
        return renderCompletionStep();
      default:
        return renderCompletionStep();
    }
  };

  // KI-Beratung anfordern
  const requestAiAdvice = async (impulseCheck: Partial<ImpulseCheck>) => {
    if (!aiService.hasApiKey()) {
      toast.error('KI-Funktion nicht verfügbar. Bitte kontaktiere den Administrator.');
      return;
    }

    if (aiService.getRemainingRequests() <= 0) {
      const resetTime = aiService.getRateLimitResetTime();
      toast.error(`Limit für KI-Anfragen erreicht. Bitte versuche es nach ${resetTime.toLocaleTimeString()} erneut.`);
      return;
    }

    setIsAiProcessing(true);
    try {
      // Erstelle einen Prompt basierend auf den eingegebenen Daten
      const prompt = `
Du bist ein persönlicher Finanzberater, der direkte und prägnante Ratschläge gibt. Du sprichst den Nutzer direkt an (du-Form) und vermeidest förmliche Anrede wie "Liebe Leserinnen und Leser".

Hier sind die Details zum Kaufimpuls des Nutzers:
- Produkt: ${impulseCheck.item}
- Preis: ${impulseCheck.price} €
- Grund für den Kauf: ${impulseCheck.reason}
- Aktuelles Gefühl: ${FEELINGS.find(f => f.value === impulseCheck.feeling)?.label || impulseCheck.feeling}
- Erwartetes Gefühl nach dem Kauf: ${impulseCheck.future_feeling}

Gib eine knappe, hilfreiche Beratung in maximal 120 Wörtern. Verwende einen freundlichen, direkten Ton.

Deine Antwort sollte folgendes beinhalten:
1. Eine kurze Einschätzung, ob der Kauf sinnvoll erscheint oder nicht
2. 2-3 konkrete Tipps oder Alternativen
3. Eine kurze Empfehlung

Wichtig: Halte deine Antwort sehr kurz und auf den Punkt. Verzichte auf lange Einleitungen oder Schlussformeln.
`;

      // Rufe die AI-Service auf
      const advice = await aiService.optimizeBlogContent({
        content: prompt,
        mode: 'optimize',
        maxLength: 120
      });

      setAiAdvice(advice);
      setShowAiAdvice(true);
      setRemainingAiRequests(aiService.getRemainingRequests());
    } catch (error) {
      console.error('Fehler bei der KI-Beratung:', error);
      toast.error(`Fehler bei der KI-Beratung: ${error instanceof Error ? error.message : 'Unbekannter Fehler'}`);
    } finally {
      setIsAiProcessing(false);
    }
  };

  // Content für den letzten Schritt (Zusammenfassung) erweitern
  const renderCompletionStep = () => {
    return (
      <div className="space-y-4">
        <div className="text-center">
          <h3 className="text-base font-medium text-gray-900 mb-1">Zusammenfassung deines Impulses</h3>
          <p className="text-xs text-gray-600 italic">{getRandomQuote('completed')}</p>
        </div>
        
        <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
          <div className="grid grid-cols-2 gap-3 text-xs">
          <div>
            <span className="font-medium text-gray-700">Produkt:</span>
              <p className="text-gray-900">{currentImpulse.item}</p>
          </div>
          
          <div>
            <span className="font-medium text-gray-700">Preis:</span>
              <p className="text-gray-900">{currentImpulse.price} €</p>
          </div>
          
          <div>
            <span className="font-medium text-gray-700">Grund:</span>
              <p className="text-gray-900">{currentImpulse.reason}</p>
          </div>
          
          <div>
            <span className="font-medium text-gray-700">Gefühl:</span>
              <p className="text-gray-900">
              {FEELINGS.find(f => f.value === currentImpulse.feeling)?.icon} {' '}
              {FEELINGS.find(f => f.value === currentImpulse.feeling)?.label}
            </p>
          </div>
          
          <div className="col-span-2">
            <span className="font-medium text-gray-700">Zukünftiges Gefühl:</span>
              <p className="text-gray-900">{currentImpulse.future_feeling}</p>
            </div>
          </div>
        </div>
        
        <Alert className="bg-orange-50 border-orange-200 py-2 px-3 rounded-lg">
          <Clock className="h-4 w-4 text-orange-600" />
          <AlertTitle className="text-orange-800 text-sm font-medium">24-Stunden Abkühlphase</AlertTitle>
          <AlertDescription className="text-orange-700 text-xs">
            Nach dem Speichern startet eine 24-Stunden Abkühlphase.
          </AlertDescription>
        </Alert>

        {/* KI-Beratung Button und Ergebnis */}
        {!showAiAdvice && !isAiProcessing && (
          <div className="flex flex-col items-center space-y-2">
            <Button 
              onClick={() => requestAiAdvice(currentImpulse)}
              className="gap-2 bg-gradient-to-r from-purple-500 to-purple-600 hover:from-purple-600 hover:to-purple-700 text-white shadow-md px-4 py-2 text-sm font-medium rounded-lg"
              disabled={isAiProcessing}
              size="sm"
            >
              <Sparkles className="h-4 w-4" />
              KI um Rat fragen
            </Button>
            <p className="text-xs text-gray-500 text-center max-w-md">
              Deine Daten werden zur Analyse an OpenAI übermittelt. <a href="/privacy" className="text-purple-600 hover:underline">Datenschutz</a>
            </p>
          </div>
        )}

        {isAiProcessing && (
          <div className="flex flex-col items-center justify-center space-y-2 p-4 border border-purple-200 rounded-lg bg-purple-50">
            <Loader2 className="h-6 w-6 text-purple-500 animate-spin" />
            <p className="text-purple-800 text-center text-sm">
              Die KI analysiert deinen Kaufimpuls...
            </p>
          </div>
        )}

        {showAiAdvice && !isAiProcessing && aiAdvice && (
          <div className="p-3 border border-purple-200 rounded-lg bg-purple-50">
            <div className="flex items-start gap-2 mb-2">
              <div className="w-6 h-6 bg-purple-600 rounded-lg flex items-center justify-center flex-shrink-0">
                <BrainCircuit className="h-3 h-3 text-white" />
              </div>
              <div>
                <h4 className="font-medium text-purple-800 text-sm">Persönliche KI-Beratung</h4>
                <p className="text-xs text-purple-600 mb-1">
                  Basierend auf deinen Angaben:
                </p>
              </div>
            </div>
            <div className="text-xs text-gray-700 whitespace-pre-line max-h-40 overflow-y-auto pr-2 bg-white rounded p-2 border border-purple-200">
              {aiAdvice}
            </div>
            <div className="mt-2 flex justify-end">
              <p className="text-xs text-purple-600">
                {remainingAiRequests} von 10 KI-Anfragen pro Stunde verfügbar
              </p>
            </div>
          </div>
        )}

        <div className="flex justify-between pt-2 border-t border-gray-200">
          <Button
            type="button"
            variant="outline"
            onClick={() => setStep(4)}
            size="sm"
            className="border-gray-200 hover:border-gray-300 rounded-lg h-8"
          >
            Zurück
          </Button>
          <Button
            type="button"
            onClick={handleSaveImpulseCheck}
            disabled={loading}
            size="sm"
            className="bg-orange-600 hover:bg-orange-700 text-white rounded-lg h-8"
          >
            {loading ? (
              <>
                <Loader2 className="w-3 h-3 animate-spin mr-2" />
                Speichern...
              </>
            ) : (
              <>Speichern und Abkühlen</>
            )}
          </Button>
        </div>
      </div>
    );
  };

  return (
    <>
      <div className="relative">
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => setOpen(true)}
                className="h-10 w-10 p-0 bg-white border border-gray-200 hover:bg-orange-50 hover:border-orange-300 transition-all duration-200 text-orange-600 rounded-xl shadow-sm hover:shadow-md"
                title="Impulskauf-Check"
              >
                <BrainCircuit className="h-4 w-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              <p>Impulskauf-Check starten</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
        
        {/* Badge für aktive Abkühlphasen */}
        {activeImpulses.length > 0 && (
          <Badge 
            className="absolute -top-2 -right-2 h-5 min-w-5 p-0 flex items-center justify-center text-xs bg-orange-500 hover:bg-orange-500 cursor-pointer border border-orange-600 rounded-full"
            onClick={() => setShowAllImpulses(true)}
          >
            {activeImpulses.length}
          </Badge>
        )}
      </div>

      {/* Mini-Anzeige für aktive Abkühlphasen */}
      {activeImpulses.length > 0 && (
        <div className="fixed bottom-4 right-4 z-40 sm:max-w-[300px] shadow-lg rounded-xl overflow-hidden mini-impulse-display">
          <div 
            className="bg-white border border-gray-200 rounded-xl p-3 cursor-pointer hover:shadow-xl hover:border-orange-300 transition-all duration-300"
            onClick={() => setShowAllImpulses(true)}
          >
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <div className="w-6 h-6 bg-orange-100 rounded-lg flex items-center justify-center">
                  <Clock className="w-3 h-3 text-orange-600" />
                </div>
                <span className="text-sm font-medium text-gray-900">Aktive Abkühlphase</span>
              </div>
              <Button 
                variant="ghost" 
                className="h-6 w-6 p-0 rounded-full hover:bg-gray-100"
                onClick={(e) => {
                  e.stopPropagation();
                  const element = document.querySelector('.mini-impulse-display');
                  if (element) {
                    element.classList.add('fadeOut');
                    setTimeout(() => {
                      element.classList.add('hidden');
                    }, 300);
                  }
                }}
              >
                <span className="sr-only">Schließen</span>
                <XCircle className="h-4 w-4 text-gray-500" />
              </Button>
            </div>
            
            <div className="bg-gray-50 rounded-lg p-2.5 border border-gray-200">
              <div className="flex justify-between items-start">
                <div>
                  <div className="font-medium text-sm text-gray-900">{activeImpulses[0].item}</div>
                  <div className="text-xs text-gray-500">{getCooldownTimeLeft(activeImpulses[0].cooldown_ends_at)}</div>
                </div>
                <div className="text-gray-900 font-medium text-sm">{activeImpulses[0].price}€</div>
              </div>
            </div>
            
            {activeImpulses.length > 1 && (
              <div className="text-xs text-gray-600 text-center mt-2">
                + {activeImpulses.length - 1} weitere aktive Abkühlphasen
              </div>
            )}
          </div>
        </div>
      )}

      {/* Impuls-Check Dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-[400px] p-4 max-h-[90vh] overflow-y-auto bg-white border border-gray-200 rounded-xl shadow-xl">
          <DialogHeader className="pb-3">
            <div className="w-10 h-10 bg-gradient-to-br from-orange-400 to-orange-500 rounded-xl flex items-center justify-center mx-auto mb-3">
              <BrainCircuit className="w-5 h-5 text-white" />
            </div>
            <DialogTitle className="text-lg font-medium text-gray-900 text-center">Impuls-Check</DialogTitle>
            <DialogDescription className="text-sm text-gray-600 text-center">
              Dieser kurze Check hilft dir, Kaufimpulse zu hinterfragen
            </DialogDescription>
          </DialogHeader>

          <div className="mt-2">
            <Progress value={(step / 5) * 100} className="h-2 mb-4" />
            
            <form onSubmit={(e) => {
              e.preventDefault();
              handleNext();
            }}>
              {getStepContent()}
            </form>
          </div>
        </DialogContent>
      </Dialog>

      {/* Alle Impulse Dialog */}
      <Dialog open={showAllImpulses} onOpenChange={setShowAllImpulses}>
        <DialogContent className="sm:max-w-[600px] bg-white border border-gray-200 rounded-xl shadow-xl">
          <DialogHeader className="pb-3">
            <div className="w-10 h-10 bg-gradient-to-br from-orange-400 to-orange-500 rounded-xl flex items-center justify-center mx-auto mb-3">
              <BrainCircuit className="w-5 h-5 text-white" />
            </div>
            <DialogTitle className="text-lg font-medium text-gray-900 text-center">Deine Impulse</DialogTitle>
            <DialogDescription className="text-sm text-gray-600 text-center">
              Übersicht deiner Kaufimpulse und Einsparungen
            </DialogDescription>
          </DialogHeader>
          
          <div className="mt-2">
            {/* Tab Navigation */}
            <div className="flex mb-4 bg-gray-50 rounded-lg p-1 border border-gray-200">
              <button
                className={`flex-1 px-3 py-2 text-sm font-medium rounded-md transition-all ${
                  activeTab === 'active' 
                    ? 'bg-white text-gray-900 shadow-sm border border-gray-200' 
                    : 'text-gray-600 hover:text-gray-900'
                }`}
                onClick={() => setActiveTab('active')}
              >
                Aktiv ({activeImpulses.length})
              </button>
              <button
                className={`flex-1 px-3 py-2 text-sm font-medium rounded-md transition-all ${
                  activeTab === 'completed' 
                    ? 'bg-white text-gray-900 shadow-sm border border-gray-200' 
                    : 'text-gray-600 hover:text-gray-900'
                }`}
                onClick={() => setActiveTab('completed')}
              >
                Abgeschlossen ({completedImpulses.length})
              </button>
            </div>
            
            <div className="space-y-3 max-h-[400px] overflow-y-auto p-1">
              {activeTab === 'active' ? (
                activeImpulses.length > 0 ? (
                  activeImpulses.map((impulse) => (
                    <Card key={impulse.id} className="border border-gray-200 rounded-xl hover:border-orange-300 hover:shadow-md transition-all">
                      <CardHeader className="pb-2 pt-3">
                        <div className="flex justify-between items-center">
                          <CardTitle className="text-base font-medium text-gray-900">{impulse.item}</CardTitle>
                          <span className="text-gray-900 font-medium">{impulse.price}€</span>
                        </div>
                        <CardDescription className="text-xs text-gray-500">
                          {new Date(impulse.created_at || '').toLocaleDateString()}
                        </CardDescription>
                      </CardHeader>
                      <CardContent className="pb-3 pt-0">
                        <div className="text-sm mb-2 text-gray-700">
                          <strong>Grund:</strong> {impulse.reason}
                        </div>
                        <div className="text-sm mb-2 text-gray-700">
                          <strong>Gefühl:</strong> {FEELINGS.find(f => f.value === impulse.feeling)?.icon} {FEELINGS.find(f => f.value === impulse.feeling)?.label || impulse.feeling}
                        </div>
                        <div className="text-sm mb-3 text-gray-700">
                          <strong>Zukünftiges Gefühl:</strong> {impulse.future_feeling}
                        </div>
                        <div className="text-sm text-orange-600 flex items-center gap-1 bg-orange-50 px-2 py-1 rounded-lg border border-orange-200">
                          <Clock className="w-4 h-4" />
                          {getCooldownTimeLeft(impulse.cooldown_ends_at)}
                        </div>
                      </CardContent>
                      <CardFooter className="pt-0 pb-3">
                        <div className="flex space-x-2 w-full">
                          <Button 
                            variant="default" 
                            size="sm"
                            className="flex-1 h-8 bg-emerald-600 hover:bg-emerald-700 border border-emerald-600"
                            onClick={() => handleCompleteImpulse(impulse.id || '')}
                          >
                            <CheckCircle2 className="w-3 h-3 mr-2" />
                            Nicht kaufen
                          </Button>
                          <Button 
                            variant="outline" 
                            size="sm"
                            className="flex-1 h-8 border-gray-200 hover:border-gray-300"
                            onClick={() => handlePurchaseAnyway(impulse.id || '')}
                          >
                            <CreditCard className="w-3 h-3 mr-2" />
                            Trotzdem kaufen
                          </Button>
                        </div>
                      </CardFooter>
                    </Card>
                  ))
                ) : (
                  <div className="text-center py-8 text-gray-500 bg-gray-50 rounded-xl border border-gray-200">
                    <BrainCircuit className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                    <p>Keine aktiven Impulse vorhanden</p>
                  </div>
                )
              ) : completedImpulses.length > 0 ? (
                completedImpulses.map((impulse) => (
                  <Card key={impulse.id} className={`border rounded-xl transition-all ${
                    impulse.status === 'completed' 
                      ? 'bg-emerald-50 border-emerald-200 hover:border-emerald-300' 
                      : 'bg-gray-50 border-gray-200 hover:border-gray-300'
                  }`}>
                    <CardHeader className="pb-2 pt-3">
                      <div className="flex justify-between items-center">
                        <CardTitle className="text-base font-medium text-gray-900 flex items-center gap-2">
                          {impulse.status === 'completed' ? (
                            <div className="w-5 h-5 bg-emerald-600 rounded-full flex items-center justify-center">
                              <CheckCircle2 className="w-3 h-3 text-white" />
                            </div>
                          ) : (
                            <div className="w-5 h-5 bg-gray-400 rounded-full flex items-center justify-center">
                              <CreditCard className="w-3 h-3 text-white" />
                            </div>
                          )}
                          {impulse.item}
                        </CardTitle>
                        <span className="font-medium text-gray-900">
                          {impulse.price}€
                        </span>
                      </div>
                      <CardDescription className="text-xs text-gray-500">
                        {new Date(impulse.created_at || '').toLocaleDateString()}
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="pb-3 pt-0">
                      <div className="text-sm mb-2 text-gray-700">
                        <strong>Grund:</strong> {impulse.reason}
                      </div>
                      <div className="text-sm mb-2 text-gray-700">
                        <strong>Gefühl:</strong> {FEELINGS.find(f => f.value === impulse.feeling)?.icon} {FEELINGS.find(f => f.value === impulse.feeling)?.label || impulse.feeling}
                      </div>
                      <div className="text-sm mb-3 text-gray-700">
                        <strong>Zukünftiges Gefühl:</strong> {impulse.future_feeling}
                      </div>
                      {impulse.status === 'completed' && (
                        <div className="mt-2 text-emerald-700 text-sm font-medium bg-emerald-100 px-2 py-1 rounded-lg border border-emerald-200">
                          Erfolgreich gespart! ✅
                        </div>
                      )}
                      {impulse.status === 'purchased' && (
                        <div className="mt-2 text-gray-600 text-sm bg-gray-100 px-2 py-1 rounded-lg border border-gray-200">
                          Gekauft nach Abkühlphase
                        </div>
                      )}
                    </CardContent>
                  </Card>
                ))
              ) : (
                <div className="text-center py-8 text-gray-500 bg-gray-50 rounded-xl border border-gray-200">
                  <CheckCircle2 className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                  <p>Keine abgeschlossenen Impulse vorhanden</p>
                </div>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Stil für Animationen */}
      <style>{fadeOutStyle}</style>
    </>
  );
};
